import { z } from 'zod'

/**
 * What a venue calls a piece of its furniture — "Vendor-Rental", "Ceremony
 * only", or whatever else that venue sorts by.
 *
 * A piece carries a list, because one piece is several things at once: a chair
 * can be the vendor's *and* only used for ceremonies. Free text rather than a
 * fixed set — the labels this started from describe how the money works, and
 * that is one of several things worth recording. What keeps the set from
 * sprawling is not a constraint but suggestion: the app offers the tags already
 * in use, so the easiest thing to do is reuse one.
 */
export const FURNITURE_TAG_MAX = 40

/** A piece cannot be an endless list of labels; past this it is a description. */
export const FURNITURE_TAGS_PER_ITEM_MAX = 12

/**
 * Offered when a venue has not tagged anything yet, so the first person to try
 * is not staring at an empty box. A starting point, not a vocabulary.
 */
export const SUGGESTED_FURNITURE_TAGS = ['Venue-Included', 'Venue-Extra Charge', 'Vendor-Rental'] as const

/**
 * Tidy a typed tag: trim the ends and collapse runs of whitespace.
 *
 * Deliberately does not touch case or punctuation. "vendor rental" and
 * "Vendor-Rental" are left as the different strings they are, because deciding
 * they are the same means deciding which one wins, and a venue that has settled
 * on its own spelling should not have it rewritten.
 */
function normalizeFurnitureTag(raw: string): string | null {
  const tidied = raw.trim().replace(/\s+/g, ' ')
  return tidied === '' ? null : tidied
}

/**
 * The tag in `existing` that differs from this one only by case, if any.
 *
 * This is what stops free text becoming the mess it usually becomes. Typing
 * "vendor-rental" where "Vendor-Rental" is already in use adopts the existing
 * spelling rather than opening a second tag that reads identically in a list.
 * A genuinely new tag is still just a new tag.
 */
export function matchExistingTag(tag: string, existing: readonly string[]): string | null {
  const folded = tag.toLocaleLowerCase()
  return existing.find((e) => e.toLocaleLowerCase() === folded) ?? null
}

/**
 * Settle a typed tag into a list: tidied, spelled the way this venue already
 * spells it, and not added twice.
 *
 * Returns the list unchanged when the tag is empty or already on the piece —
 * adding a tag something already carries is a no-op, not an error to report.
 */
export function addFurnitureTag(current: readonly string[], raw: string, known: readonly string[]): string[] {
  const tidy = normalizeFurnitureTag(raw)
  if (tidy === null) return [...current]
  const settled = matchExistingTag(tidy, known) ?? tidy
  return matchExistingTag(settled, current) === null ? [...current, settled] : [...current]
}

export const furnitureTagSchema = z.string().trim().min(1).max(FURNITURE_TAG_MAX)

export const furnitureTagsSchema = z.array(furnitureTagSchema).max(FURNITURE_TAGS_PER_ITEM_MAX)

/**
 * The tags a listing is filtered by.
 *
 * A single `tag=x` arrives as a string and a repeated `tag=x&tag=y` as an
 * array, so both are accepted and normalized to a list. Comma-joining was the
 * alternative and is wrong here: a tag is free text and may contain a comma.
 *
 * "No tags yet" is asked for with a separate `untagged` parameter rather than a
 * reserved value in this one. It was a reserved value, and a venue can name a
 * tag anything: one named "untagged" made its own filter return every piece
 * that did *not* carry it. A question about the absence of tags is a different
 * question, and it now has its own place to be asked.
 */
export const furnitureTagFilterSchema = z
  .union([furnitureTagSchema, z.array(furnitureTagSchema)])
  .transform((v) => (Array.isArray(v) ? v : [v]))

export type FurnitureTagFilter = z.infer<typeof furnitureTagFilterSchema>

/**
 * The client-side marker for the "Not set" option in a filter menu.
 *
 * Only ever held in the browser's own selection state, and translated into the
 * `untagged` parameter on the way out — it deliberately cannot be a tag,
 * because `furnitureTagSchema` caps a tag at {@link FURNITURE_TAG_MAX} and this
 * is longer.
 */
export const UNTAGGED = '\u0000untagged\u0000-not-a-tag-and-cannot-be-typed-as-one' as const
